import classnames from 'classnames';
import React, { forwardRef, JSX, ReactNode, Ref } from 'react';

import { Button, ButtonSizes, SharedButtonProps, SmallButtonTypes, StandardButtonTypes } from '@eika/button';
import { CheckmarkAnimation, CheckmarkAnimationTypes } from '@eika/checkmark-animation';
import { SimpleSpinner } from '@eika/loader';

import './style.scss';

type BasicButtonProps = React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>;

export interface ButtonWithSpinnerProps extends BasicButtonProps, SharedButtonProps {
    ariaLabel?: string;
    // @default 'positive'
    buttonType?: StandardButtonTypes | SmallButtonTypes;
    // @default 'standard'
    size?: ButtonSizes;
    children: ReactNode;
    className?: string;
    disabled?: boolean;
    showSpinner?: boolean;
    showSuccess?: boolean;
    // @default 'Fullført'
    successAriaLabel?: string;
}

function wrapIfText(children: ReactNode) {
    if (typeof children === 'string') {
        return <span>{children}</span>;
    }
    return children;
}

const ButtonWithSpinner = forwardRef(
    (
        {
            successAriaLabel = 'Fullført',
            children,
            className,
            disabled,
            showSpinner,
            showSuccess,
            size,
            ...rest
        }: ButtonWithSpinnerProps,
        ref: Ref<HTMLButtonElement>,
    ): JSX.Element => {
        const showStatus = showSpinner || showSuccess;
        return (
            <Button
                className={classnames('sgw-button-with-spinner', className, {
                    'sgw-button-with-spinner__with-status': showStatus,
                })}
                disabled={showStatus || disabled}
                {...rest}
                role={showStatus ? 'status' : undefined}
                aria-busy={showSpinner && !showSuccess}
                size={size}
                ref={ref}
            >
                <span aria-live="polite">
                    {showStatus && showSuccess && <span className="screen-reader-text">{successAriaLabel}</span>}
                </span>
                {wrapIfText(children)}
                {showStatus && (
                    <span className="sgw-button-with-spinner__status-wrapper">
                        {showSpinner && !showSuccess && <SimpleSpinner className="sgw-button-with-spinner__spinner" />}
                        {showSuccess && <CheckmarkAnimation checkmarkAnimationType={CheckmarkAnimationTypes.INLINE} />}
                    </span>
                )}
            </Button>
        );
    },
);

export default ButtonWithSpinner;
